import browserCollections from "fumadocs-mdx:collections/browser";
import { SiGithub } from "@icons-pack/react-simple-icons";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useFumadocsLoader } from "fumadocs-core/source/client";
import { DocsLayout } from "fumadocs-ui/layouts/docs";
import {
	DocsBody,
	DocsDescription,
	DocsPage,
	DocsTitle,
} from "fumadocs-ui/layouts/docs/page";
import defaultMdxComponents from "fumadocs-ui/mdx";
import { Mermaid } from "@/components/mdx/mermaid";
import { baseOptions } from "@/lib/layout.shared";
import { source } from "@/lib/source";

export const Route = createFileRoute("/docs/$")({
	component: Page,
	loader: async ({ params }) => {
		const slugs = params._splat?.split("/") ?? [];
		const data = await serverLoader({ data: slugs });
		await clientLoader.preload(data.path);
		return data;
	},
	head: ({ loaderData }) => {
		if (!loaderData) return {};
		const ogImageUrl = `/docs-og/${loaderData.slugs.join("/")}/image.png`;

		return {
			meta: [
				{
					title: loaderData.title,
				},
				{
					name: "description",
					content: loaderData.description,
				},
				{
					property: "og:title",
					content: loaderData.title,
				},
				{
					property: "og:description",
					content: loaderData.description,
				},
				{
					property: "og:image",
					content: ogImageUrl,
				},
				{
					property: "og:image:width",
					content: "1200",
				},
				{
					property: "og:image:height",
					content: "630",
				},
				{
					name: "twitter:card",
					content: "summary_large_image",
				},
				{
					name: "twitter:title",
					content: loaderData.title,
				},
				{
					name: "twitter:description",
					content: loaderData.description,
				},
				{
					name: "twitter:image",
					content: ogImageUrl,
				},
			],
		};
	},
});

const serverLoader = createServerFn({
	method: "GET",
})
	.inputValidator((slugs: string[]) => slugs)
	.handler(async ({ data: slugs }) => {
		const page = source.getPage(slugs);
		if (!page) throw notFound();

		return {
			path: page.path,
			slugs,
			title: page.data.title,
			description: page.data.description,
			pageTree: await source.serializePageTree(source.getPageTree()),
		};
	});

const clientLoader = browserCollections.docs.createClientLoader({
	component({ toc, frontmatter, default: MDX }) {
		// biome-ignore lint/correctness/useHookAtTopLevel: <false positive>
		const data = Route.useLoaderData();
		return (
			<DocsPage toc={toc}>
				<DocsTitle>{frontmatter.title}</DocsTitle>
				<DocsDescription className="mb-0">
					{frontmatter.description}
				</DocsDescription>

				<a
					href={`https://github.com/TheNextLvl-net/docs/blob/main/content/docs/${data.path}`}
					rel="noreferrer noopener"
					target="_blank"
					className="mb-8 w-fit border flex items-center gap-2 rounded-md p-2 font-medium text-xs text-fd-secondary-foreground hover:text-fd-accent-foreground hover:bg-fd-accent"
				>
					<SiGithub className="size-4" /> Edit on GitHub
				</a>
				<DocsBody>
					<MDX
						components={{
							...defaultMdxComponents,
							Mermaid,
						}}
					/>
				</DocsBody>
			</DocsPage>
		);
	},
});

function Page() {
	const data = Route.useLoaderData();
	const { pageTree } = useFumadocsLoader(data);

	return (
		<DocsLayout {...baseOptions} tree={pageTree}>
			{clientLoader.useContent(data.path)}
		</DocsLayout>
	);
}
